import styles from './index.less';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import {
  queryComputedPaymentBillTotalAmount,
  queryCustomerPaymentBillList,
} from '@/pages/PaymentBill/service';
import { useModel } from '@umijs/max';
import {
  BillItemType,
  billTypeMap,
  PaymentBillListType,
} from '@/pages/PaymentBill/interface';
import NoData from '@/components/NoData';
import { ArrowUp, ArrowDown } from '@react-vant/icons';
import { Skeleton, Checkbox, Divider } from 'react-vant';

interface BillInfoProps {
  getAllAmount: (value: string) => void;

  refresh: boolean;
  getDiscountAmount: (value: number) => void;
  getPaymentBillData: (value: any) => void;
}

const BillInfo: FC<BillInfoProps> = (props) => {
  const {
    getAllAmount,

    refresh,
    getDiscountAmount,
    getPaymentBillData,
  } = props;

  const { defaultRoom } = useModel('useDefaultRoom', (model) => ({
    defaultRoom: model.defaultRoom,
  }));
  const { defaultCustomerId } = useModel('useDefaultCustomer', (model) => ({
    defaultCustomerId: model.defaultCustomerId,
  }));

  const [loading, setLoading] = useState<boolean>(false);
  const [customerBillList, setCustomerBillList] = useState<
    PaymentBillListType[]
  >([]);
  const [isDeduction, setIsDeduction] = useState<boolean>(true);

  const handleShowBill = (feeItemId: string) => {
    setCustomerBillList((prevState) => {
      return prevState.map((item) => {
        if (item.feeItemId === feeItemId) {
          item.expand = !item.expand;
        }
        return item;
      });
    });
  };

  const handleShowBillDetail = (feeItemId: string, billNo: string) => {
    setCustomerBillList((prevState) => {
      return prevState.map((item) => {
        if (item.feeItemId === feeItemId) {
          item.bills = item.bills.map((bill) => {
            if (bill.billNo === billNo) {
              bill.expand = !bill.expand;
            } else {
              bill.expand = false;
            }
            return bill;
          });
        }
        return item;
      });
    });
  };

  const loadCustomerPaymentBillList = () => {
    setLoading(true);
    queryCustomerPaymentBillList({
      customerId: defaultCustomerId,
      roomId: defaultRoom?.roomId,
    })
      .then((res) => {
        if (res?.code === 200) {
          if (res?.data?.METER && res?.data?.GENERAL) {
            const data: PaymentBillListType[] = res?.data?.METER.concat(
              res?.data?.GENERAL,
            );
            setCustomerBillList(
              data.map((item) => ({
                ...item,
                checked: true,
                expand: false,
                bills: item.bills?.map((bill) => ({
                  ...bill,
                  checked: true,
                  expand: false,
                  unitPriceFormat:  bill.dynamicFeeInstance.map(item => `${item?.price}(${item?.optionName})`).join(';')
                })),
              })),
            );
          }
        }
      })
      .finally(() => setLoading(false));
  };

  const handleChecked = useCallback(
    (item: PaymentBillListType, checked: boolean) => {
      setCustomerBillList((prevState) => {
        return prevState.map((prevItem) => {
          if (prevItem.feeItemId === item.feeItemId) {
            prevItem.checked = checked;
            prevItem.bills?.forEach((bill) => {
              bill.checked = checked;
            });
          }
          return prevItem;
        });
      });
    },
    [customerBillList],
  );

  const handleBillChecked = useCallback(
    (item: BillItemType, checked: boolean) => {
      const data = customerBillList.find((a) => a.feeItemId === item.feeItemId);
      setCustomerBillList((prevState) => {
        return prevState.map((prevItem) => {
          if (prevItem.feeItemId === data?.feeItemId) {
            prevItem.bills?.forEach((bill) => {
              if (bill.sequenceNbr === item.sequenceNbr) {
                bill.checked = checked;
              }
            });
            const allNotChecked = prevItem.bills?.every(
              (bill) => !bill.checked,
            );
            if (checked) {
              prevItem.checked = true;
            } else {
              prevItem.checked = !allNotChecked;
            }
          }
          return prevItem;
        });
      });
    },
    [customerBillList],
  );

  const loadComputedBillAmount = async () => {
    const data = customerBillList
      .filter((item) => item.checked)
      .map((item) =>
        item.bills.filter((bill) => bill.checked).map((bill) => bill.billNo),
      )
      .flat();
    const res = await queryComputedPaymentBillTotalAmount({
      billNos: data,
      customerId: defaultCustomerId,
      isDeduction: isDeduction,
      paymentAmount: 0,
      surplusMethod: 'RECHARGE',
    });
    if (res?.code === 200) {
      getAllAmount(res?.data?.totalAmount);
      getPaymentBillData({
        ...res.data,
        customerId: defaultCustomerId,
      });
      getDiscountAmount(Number(res?.data?.discountAmount));
    }
  };

  useEffect(() => {
    if (customerBillList.length) {
      loadComputedBillAmount();
    }
  }, [customerBillList, isDeduction]);

  useEffect(() => {
    if (defaultCustomerId && defaultRoom?.roomId) {
      loadCustomerPaymentBillList();
    }
    if (refresh) {
      loadCustomerPaymentBillList();
    }
  }, [defaultCustomerId, defaultRoom?.roomId, refresh]);

  return (
    <div className={styles.container}>
      <div className={styles.cardContainer}>
        <p>使用余额：</p>
        <Checkbox
          checked={isDeduction}
          shape={'square'}
          style={{ borderRadius: 6 }}
          onChange={(value) => setIsDeduction(value)}
        />
      </div>
      {customerBillList?.length ? (
        <>
          {(customerBillList || []).map((item) => (
            <div key={item.feeItemId}>
              {loading && <Skeleton />}
              <div className={styles.date}>
                <span>{item.billMonths}</span>
                <span>{item.feeItemName}</span>
              </div>
              <div className={styles['bill-content']}>
                <div className={styles['bill-title']}>
                  <div className={styles['bill-title-left']}>
                    <Checkbox
                      onChange={(value) => handleChecked(item, value)}
                      checked={item.checked}
                      shape={'square'}
                      style={{ borderRadius: 6 }}
                    />
                    <span className={styles.symbol}>￥</span>
                    <p className={styles.amount}>{item.totalAmount}</p>
                  </div>
                  <div onClick={() => handleShowBill(item.feeItemId)}>
                    {item.expand ? <ArrowUp /> : <ArrowDown />}
                  </div>
                </div>
                <div className={styles['bill-tips']}>
                  共{item.bills.length}笔费用
                </div>
                {item.expand && (
                  <div className={styles['bill-list']}>
                    <Divider />
                    <div className={styles['bill-container']}>
                      <div className={styles.title}>
                        <p>账单月份</p>
                        <p>费用类型</p>
                        <p>应缴金额</p>
                        <p>操作</p>
                      </div>
                      <div className={styles.content}>
                        {item.bills.map((bill) => (
                          <Fragment key={bill.sequenceNbr}>
                            <div
                              className={styles['list-container']}
                              key={bill.sequenceNbr}
                            >
                              <div>
                                <Checkbox
                                  checked={bill.checked}
                                  onChange={(value) =>
                                    handleBillChecked(bill, value)
                                  }
                                  shape={'square'}
                                  style={{
                                    borderRadius: 6,
                                    marginRight: 4,
                                    '--rv-checkbox-size': 14,
                                  }}
                                />
                                {bill.billMonth}
                              </div>
                              <p>{billTypeMap[bill.billType]}</p>
                              <p>{bill.payableAmount}</p>
                              <p
                                onClick={() =>
                                  handleShowBillDetail(
                                    item.feeItemId,
                                    bill.billNo,
                                  )
                                }
                                style={{ color: '#3f45ff' }}
                              >
                                {bill.expand ? '收起' : '展开'}
                              </p>
                            </div>
                            <Divider style={{ '--rv-divider-margin': 0 }} />
                            {bill.expand && (
                              <div className={styles['bill-detail']}>
                                <div className={styles.detail}>
                                  <p>单价：</p>
                                  <span>{bill.unitPriceFormat}</span>
                                </div>
                                <div className={styles.detail}>
                                  <p>金额：</p>
                                  <span>{bill.totalAmount}</span>
                                </div>
                                <div className={styles.detail}>
                                  <p>计费开始日期：</p>
                                  <span>{bill.billStartDate}</span>
                                </div>
                                <div className={styles.detail}>
                                  <p>计费结束日期：</p>
                                  <span>{bill.billEndDate}</span>
                                </div>
                                 <div className={styles.detail}>
                                  <p>计费规则：</p>
                                  <span>{bill.amountCalRule}</span>
                                </div>
                              </div>
                            )}
                          </Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </>
      ) : (
        <NoData />
      )}
    </div>
  );
};
export default BillInfo;
